import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDomainConfig } from "../../config/config";
import { OrderSummaryContext } from "../../contexts/order-summary-context";
import { useCart } from "../../hooks/useCart";
import AddedExtras from "./AddedExtras";
import { ReactComponent as CheckIcon } from "../../images/global/check.svg";
import {ReactComponent as BasketIcon} from "../../images/global/basket-logo.svg";
import { ABContext } from "../../contexts/ab-context";
import HotelMiniCart from "../HotelMinicart/HotelMinicart";
import { formatCurrency, getGeo } from "../../utils/formatCurrency";

const SubTotal = (props) => {
  const config = getDomainConfig();
  const { t } = useTranslation('common');

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("SubTotal", ".module.scss");
  }, [config.brand]);
  /* END LOADING THE BRANDED SCSS */

  const abContext = useContext(ABContext);

  // CRO-1381-checkout-redesign
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  // CRO-1381-checkout-redesign - Show/hide
  const [showSummary, setShowSummary] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  let showHideHeadingClass = "ticketSummary ticketSummaryHide";
  let showHideTableClass = styles.cro1381HideMobile;

  if (showSummary) {
    showHideHeadingClass = "ticketSummary ticketSummaryOpen";
    showHideTableClass = styles.cro1381ShowMobile;
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth > 1024) {
      setShowSummary(false);
    }
  }, [windowWidth]);
  // CRO-1381-checkout-redesign - Show/hide close

  const { orderSummary, setOrderSummary } = useContext(OrderSummaryContext);
  const [deliveryCharges, setDeliveryCharges] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);
  const [amountToPay, setAmountToPay] = useState(0);
  const { isSuccess: cartIsSuccess, data: cartContents } = useCart();
  let cartMeta = [];

  const [taxMessage, setTaxMessage] = useState('');

  const geo = getGeo();

  if (cartIsSuccess) {
    cartMeta = cartContents.meta;
  }

  // Update delivery and discounts if they's selected/updated.
  useEffect(() => {

    if (orderSummary.deliveryCharges) {
      setDeliveryCharges(
        parseFloat(orderSummary.deliveryCharges.price)
      );
    }
    const totalDiscount = parseFloat(cartMeta.total_discount).toFixed(2);
    setDiscountAmount(totalDiscount);

    const giftCardDeduction = parseFloat(cartMeta.total_gift_card_balance);
    setGiftCardAmount(giftCardDeduction)

    let sumOrderTotal = 0;
    if (cartIsSuccess) {
      sumOrderTotal = parseFloat(cartMeta.total_price);
      if (orderSummary.deliveryCharges) {
        sumOrderTotal += parseFloat(
          orderSummary.deliveryCharges.price
        );
      }
      if (totalDiscount) {
        sumOrderTotal -= parseFloat(totalDiscount);
      }
      if (cartMeta.total_gift_card_balance > 0) {
        sumOrderTotal -= parseFloat(cartMeta.total_gift_card_balance);
      }
      if (+cartMeta?.total_hotel_tax > 0) {
        sumOrderTotal += +cartMeta.total_hotel_tax;
      }
      setOrderTotal(sumOrderTotal < 0 ? 0 : sumOrderTotal.toFixed(2));
      setAmountToPay(sumOrderTotal < 0 ? 0 : sumOrderTotal.toFixed(2));
    }

    if (orderSummary.paymentOption) {
      // If deposit.
      if (orderSummary.paymentOption === "deposit") {
        let deposit = +cartMeta.payment_options.deposittotal - +cartMeta.total_gift_card_balance;

        if (+cartMeta.total_gift_card_balance >= +cartMeta.payment_options.deposittotal) {
          setAmountToPay(0);
        }
        else {
          setAmountToPay(deposit.toFixed(2));
        }
      } else if (orderSummary.paymentOption === "non-refundable-deposit") {
        setAmountToPay(cartMeta.payment_options.non_refundable_deposit?.amount.toFixed(2));
      } else {
        setAmountToPay(sumOrderTotal < 0 ? 0 : sumOrderTotal.toFixed(2));
      }
    }
  }, [orderSummary, cartIsSuccess]);

  let defaultClass = styles.amountDetails; // The generic class (in css modules format)
  let classes = defaultClass; // The classes list to show

  if (props.className) {
    // If any extra classes come through via props, turn them into css modules format too
    classes =
      classes +
      " " +
      props.className
        .split(" ")
        .map((thisStyle) => {
          return styles[thisStyle];
        })
        .join(" ");
  }

  useEffect(() => {
    setOrderSummary({...orderSummary, total: amountToPay});
  },[amountToPay, cartContents, cartIsSuccess]);

  return (
    <div className={`${classes} subTotal-sideTableHolder`} id={props.id}>
      {(!!cartMeta.is_hotel_cart && config.brand !== "Sw") && (
        <div className={styles.hotelsBasket}>
          {isCro1381 && (
            <>
              {(cartMeta.is_hotel_cart && config.brand !== "Sw") && (
                <div className={styles.cro1381BasketHotelsMobile}>
                  <a
                    href="#checkout-summary"
                    className={`${styles.cro1381TicketSummaryTrigger} ${showHideHeadingClass}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowSummary(!showSummary);
                    }}
                  ><BasketIcon /> {t('Basket')}
                  </a>
                </div>
              )}
            </>
          )}
          <div className={`${isCro1381 ? showHideTableClass : ''}`}>
            {!isCro1381 ?
              <h2>Basket</h2>
            :
              <div className={styles.basketHeader}>
                <BasketIcon />
                <h2>{t('Basket')}</h2>
              </div>
            }
            <ul id='minicart-info'>
              <li className={styles.cartItem} id='floating-cart-traveling-info'>
              {!isCro1381 ? <h3>Travelling</h3> : <h3>Summary</h3>}
                <HotelMiniCart styles={styles} />
              </li>
              <li className={styles.cartItem}>
                <AddedExtras
                  cartData={cartContents.data}
                  setCancellationMessage={props.setCancellationMessage}
                  setTaxMessage={setTaxMessage}
                />
              </li>
            </ul>
            {(!!props.cancellationMessage || !!taxMessage) && isCro1381 && (
              <div className={styles.pricePersuasionsMobile}>
                {taxMessage !== "" && (
                  <span className={styles.taxMessage}>{taxMessage}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className={isCro1381 && !!cartMeta.is_hotel_cart && styles.cro1381HotelTotalDesktop}>
        <table className={styles.table} id="floating-card-ticket-details">
          <tbody>
            {(!cartMeta.is_hotel_cart && config.brand !== "Sw") && (
              <tr className={styles.subtotal}>
                <td className={styles.item}>{t("Subtotal")} </td>
                <td className={styles.price}>
                  {formatCurrency(cartMeta.total_price, geo)}
                </td>
              </tr>
            )}
            {orderSummary.deliveryCharges && (
              <tr className={styles.delivery}>
                <td className={styles.item}>Delivery </td>
                <td className={styles.price}>
                  {formatCurrency(deliveryCharges, geo)}
                </td>
              </tr>
            )}
            {giftCardAmount > 0 && (
              <tr className={styles.delivery}>
                <td className={styles.item}>Gift card </td>
                <td className={styles.price}>
                  {" "}
                  -
                  {giftCardAmount >= cartMeta.total_price
                    ? formatCurrency(cartMeta.total_price, geo)
                    : formatCurrency(giftCardAmount.toFixed(2), geo)}
                </td>
              </tr>
            )}
            {discountAmount > 0 && (
              <tr className={styles.discount}>
                <td id="discount-amount" className={styles.item}>Discount </td>
                <td className={styles.price}>
                  -
                  {formatCurrency(discountAmount, geo)}
                </td>
              </tr>
            )}
            {(!isCro1381 || (isCro1381 && orderSummary.paymentOption !== "full")) && (
            <tr className={styles.totalprice} data-testid="total-price">
              <td className={styles.item}>{t("Total")} </td>
              <td className={styles.price}>
              {formatCurrency(orderTotal, geo) < 0 ? 0 : formatCurrency(orderTotal, geo)}
              </td>
            </tr>
            )}

            <tr className={styles.total} data-testid='total-price'>
              <td className={!isCro1381 ? styles.item : styles.cro1381Total}>{!isCro1381
              ? t("total")
              : orderSummary.paymentOption !== "full"
              ? t("total-to-pay-today")
              : t("Total")} </td>
              <td className={!isCro1381 ? styles.price : `${styles.cro1381Price} ${styles.cro1381GrandTotal}`}>
                {formatCurrency(amountToPay, geo) < 0 ? 0 : formatCurrency(amountToPay, geo)}
              </td>
            </tr>
          </tbody>
        </table>
        {((!!props.cancellationMessage || !!taxMessage) && (orderSummary.paymentOption !== "non-refundable-deposit")) && (
          <div className={styles.pricePersuasions}>
            {taxMessage !== "" && (
              <span className={styles.taxMessage}>{taxMessage}</span>
            )}
            {props.cancellationMessage !== "" && (
              <span className={styles.cancellationText}>
                <CheckIcon />
                {props.cancellationMessage}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubTotal;
